import React from "react"
import { useState, useEffect } from "react"
import type { CSSProperties, ReactNode } from "react"
import { Helmet } from "react-helmet"

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;1,400&family=DM+Mono:wght@300;400&display=swap');

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  :root {
    --sand: #f5f0e8;
    --sand-dark: #e8e0d0;
    --ink: #2a2520;
    --ink-light: #6b5f52;
    --accent: #c4744a;
    --accent-light: #e8c4a8;
    --warm-white: #faf7f2;
  }

  body {
    background-color: var(--sand);
    color: var(--ink);
    font-family: 'Lora', Georgia, serif;
  }

  .grain {
    position: fixed;
    inset: 0;
    pointer-events: none;
    opacity: 0.035;
  }
`

const s = {
  page: {
    backgroundColor: "var(--sand)",
    minHeight: "100vh",
  } as CSSProperties,
  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2.2rem 2rem",
    maxWidth: 900,
    margin: "0 auto",
    position: "relative",
  } as CSSProperties,
  navMono: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    fontWeight: 300,
    letterSpacing: "0.12em",
    color: "var(--ink-light)",
    textTransform: "uppercase" as const,
  } as CSSProperties,
  navDot: {
    width: 6,
    height: 6,
    background: "var(--accent)",
    borderRadius: "50%",
    position: "absolute" as const,
    left: "50%",
    transform: "translateX(-50%)",
  } as CSSProperties,
  main: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "0 2rem",
  } as CSSProperties,
  title: {
    fontSize: "clamp(2.5rem, 6vw, 4rem)",
    marginBottom: "1rem",
  } as CSSProperties,
  section: {
    marginBottom: "2rem",
  } as CSSProperties,
  sectionTitle: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "var(--ink-light)",
    marginBottom: "0.5rem",
  } as CSSProperties,
  text: {
    lineHeight: 1.7,
  } as CSSProperties,
  footer: {
    borderTop: "1px solid var(--sand-dark)",
    padding: "2rem",
    maxWidth: 900,
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
  } as CSSProperties,
}

/* Responsive hook */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640)
    handler()
    window.addEventListener("resize", handler)
    return () => window.removeEventListener("resize", handler)
  }, [])

  return isMobile
}

export default function PrivacyPage(): JSX.Element {
  useIsMobile()

  return (
    <div style={s.page}>
      <Helmet>
        <title>Hendrik Gruber | Privacy Policy</title>
        <meta
          name="description"
          content="Privacy policy for Hendrik Gruber's website."
        />
      </Helmet>
      <style>{styles}</style>

      <div className="grain" />

      <nav style={s.nav}>
        <span style={s.navMono}>
          <a href="/">Home</a>
        </span>
        <div style={s.navDot} />
        <span style={s.navMono}>
          <a href="/imprint">Imprint</a>
        </span>
      </nav>

      <main style={s.main}>
        <h1 style={s.title}>Privacy Policy</h1>

        <div style={s.section}>
          <p style={s.sectionTitle}>General Information</p>
          <p style={s.text}>
            This website does not collect, process, or store any personal data
            beyond what is technically necessary to display the page.
          </p>
        </div>

        <div style={s.section}>
          <p style={s.sectionTitle}>Server Log Files</p>
          <p style={s.text}>
            The hosting provider may automatically collect and store information
            in server log files, such as browser type, operating system, or time
            of access. This data is not assigned to specific persons and is used
            solely to ensure the technical operation and security of the
            website.
          </p>
        </div>

        <div style={s.section}>
          <p style={s.sectionTitle}>Contact</p>
          <p style={s.text}>
            If you contact me via email, the data you provide will be stored for
            the purpose of processing your request. This data will not be shared
            without your consent.
          </p>
        </div>

        <div style={s.section}>
          <p style={s.sectionTitle}>External Links</p>
          <p style={s.text}>
            This website contains links to external websites (e.g. LinkedIn).
            When you follow these links, the respective provider may process
            personal data. Please refer to their privacy policies for details.
          </p>
        </div>

        <div style={s.section}>
          <p style={s.sectionTitle}>Cookies & Tracking</p>
          <p style={s.text}>
            This website does not use cookies, tracking tools, or analytics
            services.
          </p>
        </div>

        <div style={s.section}>
          <p style={s.sectionTitle}>Your Rights</p>
          <p style={s.text}>
            Under applicable data protection laws, you have the right to request
            information about your stored personal data, as well as the right to
            correction, deletion, or restriction of processing.
          </p>
        </div>

        <div style={s.section}>
          <p style={s.sectionTitle}>Responsible Party</p>
          <p style={s.text}>
            Hendrik Gruber
            <br />
            Sindlinger Str. 16
            <br />
            60326 Frankfurt am Main
            <br />
            Germany
            <br />
            <br />
            Email: gruber.hendrik@gmail.com
          </p>
        </div>
      </main>

      <footer style={s.footer}>
        <span>© {new Date().getFullYear()} Hendrik Gruber</span>
        <span>Frankfurt am Main, DE</span>
      </footer>
    </div>
  )
}
